/**
 * seo-i18n-loader — lazy access to the translated SEO catalog.
 *
 * The non-English SEO catalog (seo-descriptions-i18n + its patches, ~480 KB
 * source) used to sit in the main bundle for every visitor, including English
 * ones. It now lives in its own chunk:
 *  - main.tsx awaits `ensureSeoI18n()` BEFORE the first render whenever the URL
 *    starts with a non-English language prefix, so the first render sees exactly
 *    the same translated titles/descriptions as before;
 *  - English visitors prefetch it on idle (in-app language switch stays instant);
 *  - `useSeoI18nReady()` re-renders SEOHead/DublinCoreMeta if a translation
 *    arrives after mount (safety net for client-side language switches).
 */
import { useSyncExternalStore } from 'react';
import type { PageSEODescription } from './seo-descriptions';

type Getter = (catalogKey: string, lang: string) => Partial<PageSEODescription> | undefined;

let getter: Getter | null = null;
let pending: Promise<void> | null = null;
const listeners = new Set<() => void>();

export function ensureSeoI18n(): Promise<void> {
  if (getter) return Promise.resolve();
  if (!pending) {
    pending = import('./seo-descriptions-i18n').then((m) => {
      getter = m.getTranslatedSEO;
      listeners.forEach((l) => l());
    });
    // Allow a retry after a failed chunk fetch instead of caching the rejection.
    pending.catch(() => { pending = null; });
  }
  return pending;
}

export function isSeoI18nLoaded(): boolean {
  return getter !== null;
}

/** Synchronous lookup; triggers loading when not yet available. */
export function getTranslatedSEOLazy(catalogKey: string, lang: string): Partial<PageSEODescription> | undefined {
  if (getter) return getter(catalogKey, lang);
  if (lang !== 'en') void ensureSeoI18n().catch(() => undefined);
  return undefined;
}

const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

export function useSeoI18nReady(): boolean {
  return useSyncExternalStore(subscribe, isSeoI18nLoaded, isSeoI18nLoaded);
}

const SUPPORTED = new Set(['fr','es','ar','de','it','pt','nl','pl','ru','zh','ja','ko','th','tr','vi','el','sv','da','fi','no','hu','cs','ro','id','ms','tl','bn','hi']);

/** True when the path's first segment is a supported non-English language. */
export function pathNeedsSeoI18n(pathname: string): boolean {
  const m = pathname.match(/^\/([a-z]{2})(\/|$)/);
  return !!m && SUPPORTED.has(m[1]);
}
